import { useLazyGetLatestVersionOfDocumentsByPermitRegisterId } from "../operations/queries/getLatestVersionOfDocumentsByPermitRegisterId";
import { useLazyGetDocumentTypesByOrganizationId } from "../operations/queries/getDocumentTypesByOrganizationId";
import DocumentsList from "../components/DocumentsList";
import { useDeleteDocument } from "../operations/mutations/deleteDocument";
import { useCallback, useEffect, useState } from "react";
import debounce from "lodash/debounce";
import { usePermitRegisterPolicies } from "../hooks/usePermitRegisterPolicies";
import { useAppContext } from "../hooks/useAppContext";

const DocumentsListContainer = () => {
  const { currentOrganization, currentProject, currentPermitRegister } = useAppContext();
  const [paginationInfo, setPaginationInfo] = useState({ pageSize: 0, pageIndex: 0 });
  const [searchString, setSearchString] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>();
  const { authorizedToAddDocument, authorizedToAddNewDocumentVersion, authorizedToDeleteDocument } =
    usePermitRegisterPolicies();

  const {
    getLatestVersionOfDocumentsByPermitRegisterId: getDocuments,
    data: documents,
    status: getDocumentsStatus,
    refetch: refetchDocuments,
  } = useLazyGetLatestVersionOfDocumentsByPermitRegisterId();
  const { getDocumentTypesByOrganizationId: getDocumentTypes, data: documentTypes } =
    useLazyGetDocumentTypesByOrganizationId();
  const { mutate: deleteDocumentMutate, status: deleteDocumentStatus } = useDeleteDocument();

  useEffect(() => {
    if (currentOrganization) {
      getDocumentTypes({ variables: { organizationId: currentOrganization.id } });
    }
  }, [currentOrganization, getDocumentTypes]);

  const handlePageInfoChange = useCallback(
    (pageIndex: number, pageSize: number) => {
      if (pageSize !== paginationInfo.pageSize || pageIndex !== paginationInfo.pageIndex) {
        setPaginationInfo({ pageSize, pageIndex });
      }
    },
    [paginationInfo.pageIndex, paginationInfo.pageSize]
  );

  const handleSearchDocuments = debounce((searchString: string) => {
    if (searchString !== undefined) {
      setSearchString(searchString);
    }
    setPaginationInfo({ ...paginationInfo, pageIndex: 0 });
  }, 500);

  const handleTypeFilter = (typeId: string) => {
    if (typeId !== undefined) setTypeFilter(typeId);
    setPaginationInfo({ ...paginationInfo, pageIndex: 0 });
  };

  useEffect(() => {
    if (currentPermitRegister && paginationInfo.pageSize > 0)
      getDocuments({
        variables: {
          permitRegisterId: currentPermitRegister.id,
          skip: paginationInfo.pageIndex * paginationInfo.pageSize,
          take: paginationInfo.pageSize,
          searchString,
          typeId: typeFilter,
        },
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationInfo.pageSize, paginationInfo.pageIndex, currentPermitRegister, searchString, typeFilter]);

  const handleDeleteDocument = (id: string) => {
    if (currentPermitRegister) {
      deleteDocumentMutate({
        variables: {
          id,
          permitRegisterId: currentPermitRegister.id,
        },
      }).then(() => {
        refetchDocuments && refetchDocuments();
      });
    }
  };

  const handleDocumentAdded = () => {
    refetchDocuments && refetchDocuments();
  };

  return (
    <DocumentsList
      newDocumentButtonVisible={authorizedToAddDocument}
      addNewVersionButtonVisible={authorizedToAddNewDocumentVersion}
      deleteDocumentButtonVisible={authorizedToDeleteDocument}
      documents={documents && documents.latestVersionOfDocumentsByPermitRegisterId?.items}
      pageCount={
        documents &&
        Math.ceil((documents?.latestVersionOfDocumentsByPermitRegisterId?.totalCount ?? 0) / paginationInfo.pageSize)
      }
      documentTypes={documentTypes && documentTypes.documentTypesByOrganizationId.filter((t) => !t.isArchived)}
      onPageInfoChange={handlePageInfoChange}
      onSearchDocuments={handleSearchDocuments}
      onTypeFilter={handleTypeFilter}
      getDocumentsStatus={getDocumentsStatus}
      onDeleteDocument={handleDeleteDocument}
      deleteDocumentStatus={deleteDocumentStatus}
      onDocumentAdded={handleDocumentAdded}
    ></DocumentsList>
  );
};

export default DocumentsListContainer;
